import { TextField } from '@mui/material';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { FormError } from '../../../components/hook-form/hook-form-error';
import { useEffect } from 'react';

interface Field {
  apiurl: string;
  apiParams: Record<string, any>;
  controlType: string;
  fieldDisplayName: string;
}

interface FormCurrencyInputProps {
  label: string;
  name: string;
  currencyChar?: string;
  disabled?: boolean;
  required?: boolean;
  field: Field;
  filterKey: string;
  groupKey: string;
}

interface FormatResponse {
  formattedValue?: string;
  originalValue?: string;
}

export const FormInputNumeric = ({
  name,
  label,
  currencyChar,
  disabled,
  required,
  field,
  filterKey,
  groupKey,
}: FormCurrencyInputProps): any => {
  const { control, setValue, unregister } = useFormContext();
  const { errors } = useFormState();

  const formatValue = (input?: string): FormatResponse => {
    const normalizedInput = Array.isArray(input) ? '' : input;

    if (!normalizedInput) {
      return { formattedValue: '', originalValue: normalizedInput };
    }

    // Remove any non-digit characters (including dot)
    const sanitizedValue = normalizedInput?.replace(/[^0-9]/g, '');

    return {
      formattedValue: `${sanitizedValue}`, // Add currencyChar (e.g., $) before value
      originalValue: sanitizedValue, // Return only the numeric part without any non-digits
    };
  };

  useEffect(() => {
    // Reset field value and fetch data when filterKey changes
    setValue(name, '');
  }, [field, filterKey, setValue, name]);

  useEffect(() => {
    return () => {
      unregister(`groups.${groupKey}.filters.${filterKey}.fieldValue`);
    };
  }, [groupKey, filterKey, unregister]);

  return (
    <div className="form-group">
      <Controller
        control={control}
        name={name}
        rules={{
          required: required ? `${label} is required` : false, // Apply required validation if `required` is true
        }}
        render={({ field }) => (
          <TextField
            className="formatted-input"
            variant="outlined"
            size="small"
            fullWidth
            label={label}
            disabled={disabled}
            value={formatValue(field?.value).formattedValue}
            name={name}
            onChange={(e) => {
              field?.onChange(formatValue(e.currentTarget.value).originalValue);
            }}
            onBlur={field?.onBlur}
            placeholder="0"
            inputProps={{
              pattern: '[0-9]*', // Allow only numeric input
            }}
          />
        )}
      />
      <FormError name={name} />
    </div>
  );
};

FormInputNumeric.defaultProps = {
  currencyChar: '$',
};
